/**
 * Hedged display (§12). The sim decides the tier (`readClarity`) and which
 * axes are worth a sentence (`readSignals`); these are only the words.
 */

import { type Axis, type NpcReadLayer, readClarity, readSignals } from '@sim/index.ts';

const FLAT: Record<Axis, [positive: string, negative: string]> = {
  trust: ['{name} trusts you.', "{name} doesn't trust you."],
  respect: ['{name} respects you.', "{name} has no respect for you."],
  fear: ['{name} is afraid of you.', '{name} is not afraid of you.'],
  warmth: ['{name} is warm toward you.', '{name} is cold toward you.'],
};

const HEDGED: Record<Axis, [positive: string, negative: string]> = {
  trust: ['{name} seems to trust you, more or less.', '{name} might not trust you.'],
  respect: ['{name} seems to take you seriously.', "{name} doesn't seem to take you seriously."],
  fear: ['{name} seems wary of you.', '{name} seems easy around you.'],
  warmth: ['{name} seems friendly enough.', '{name} seems cool toward you.'],
};

/** What you make of them, as lines. */
export function describeRead(name: string, read: NpcReadLayer): string[] {
  const first = name.split(' ')[0] ?? name;
  const fill = (s: string) => s.replace('{name}', first);

  if (read.acquaintance === 'stranger') return ["You don't know this person."];
  const clarity = readClarity(read);
  if (clarity === 'blind') return [fill('Hard to say what {name} makes of you.')];

  const table = clarity === 'hazy' ? HEDGED : FLAT;
  const lines = readSignals(read).map(({ axis, positive }) => fill(table[axis][positive ? 0 : 1]));
  if (lines.length === 0) {
    lines.push(fill(clarity === 'hazy' ? '{name} seems to have no strong opinion of you.' : '{name} has no strong opinion of you.'));
  }
  return lines;
}

/** How well you know them, in words. */
export function describeFamiliarity(read: NpcReadLayer): string {
  if (read.acquaintance === 'stranger') return 'A stranger.';
  if (read.acquaintance === 'seen') return "You've seen this person around.";
  switch (readClarity(read)) {
    case 'blind':
      return 'You know the name and not much else.';
    case 'hazy':
      return 'You know them a little.';
    case 'clear':
      return 'You know them well.';
  }
}
